import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { cn } from '../lib/utils';

const PRESET_COLORS = [
    '#000000', '#ffffff', '#ef4444', '#f97316', '#f59e0b', '#84cc16',
    '#22c55e', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
    '#d946ef', '#ec4899', '#f43f5e', '#64748b'
];

const ColorPicker = ({ color, onChange, className }) => {
    const [localColor, setLocalColor] = useState(color || '#000000');

    useEffect(() => {
        setLocalColor(color || '#000000');
    }, [color]);

    const handleChange = (newColor) => {
        setLocalColor(newColor);
        onChange(newColor);
    };

    return (
        <Popover>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal px-2", className)}
                >
                    <div
                        className="w-4 h-4 rounded-full mr-2 border border-gray-200"
                        style={{ backgroundColor: localColor }}
                    />
                    <span>{localColor}</span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
                <div className="grid gap-4">
                    <div className="space-y-2">
                        <h4 className="font-medium leading-none">Color Picker</h4>
                        <p className="text-sm text-muted-foreground">
                            Select a color or enter a hex code.
                        </p>
                    </div>
                    <div className="grid gap-2">
                        <div className="grid grid-cols-4 gap-2">
                            {PRESET_COLORS.map((preset) => (
                                <button
                                    key={preset}
                                    className={cn(
                                        "w-8 h-8 rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
                                        localColor === preset && "ring-2 ring-ring ring-offset-2"
                                    )}
                                    style={{ backgroundColor: preset }}
                                    onClick={() => handleChange(preset)}
                                />
                            ))}
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                            <Label htmlFor="hex" className="w-12">Hex</Label>
                            <Input
                                id="hex"
                                value={localColor}
                                onChange={(e) => handleChange(e.target.value)}
                                className="h-8"
                            />
                            <input
                                type="color"
                                value={localColor}
                                onChange={(e) => handleChange(e.target.value)}
                                className="w-8 h-8 p-0 border-0 rounded-md cursor-pointer opacity-0 absolute"
                                style={{ pointerEvents: 'none' }} // Hidden native picker for fallback if needed, but we're using the custom UI
                            />
                            {/* We can add a small trigger for the native picker if desired, but the presets + hex input is usually enough. 
                  Let's add a small button that triggers the native picker for infinite colors */}
                            <div className="relative">
                                <input
                                    type="color"
                                    value={localColor}
                                    onChange={(e) => handleChange(e.target.value)}
                                    className="w-8 h-8 p-0 border-0 rounded-md cursor-pointer opacity-0 absolute inset-0"
                                />
                                <div className="w-8 h-8 rounded-md border border-gray-200 bg-gradient-to-br from-red-500 via-green-500 to-blue-500" />
                            </div>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

export default ColorPicker;
